// Funções puras pra navegar o payload de POST /v1/indicadores.
// Formato: { emissao_id, emissao, periodos: [{ periodo, splits: [{ split_id, split_nome,
// split_tipo, fundos:{key:{...}}, indices:{key:{...}} }] }] }.
// Dentro de cada fundo/índice, "nome", "tipo_valor", "ref" e "tipo_ref" são reservados —
// todo o resto é um campo numérico dinâmico (ex.: apurado, apurado_sem_aporte, saldo_final).
// "remanescente_obra" (Fundo de Obras) também é reservado: é exibido como referência
// informativa (ver fmtRefLabel), não como campo de apuração.
// "foi_apurado*" (booleanos) dizem se a API conseguiu apurar o campo de valor
// correspondente (ver foiApuradoKey) — false = status "Não apurado", mesmo que o
// campo de valor nem venha no payload.

const RESERVED_FIELDS = new Set(['nome', 'tipo_valor', 'ref', 'tipo_ref', 'remanescente_obra']);

const FIELD_LABELS = {
  apurado: 'Apurado',
  apurado_sem_aporte: 'Sem aporte',
  apurado_com_aporte: 'Com aporte',
  saldo_inicial: 'Saldo inicial',
  saldo_final: 'Apurado',
  remanescente_obra: 'Remanescente',
};

export function fieldLabel(field) {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  return field.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// versão curta do rótulo, pra caber na sub-linha da célula da tabela (mesmo espaço
// enxuto hoje usado pra "mín."/"máx." de referência)
const FIELD_SHORT_LABELS = { remanescente_obra: 'Rem.' };
export function fieldShortLabel(field) {
  return FIELD_SHORT_LABELS[field] || fieldLabel(field);
}

// campos que não entram no valor principal (ver pickDisplayFields), mas aparecem como
// uma sub-linha extra sob o valor do indicador (mesmo estilo da referência min/max) —
// hoje só o Fundo de Obras tem um (o remanescente da obra financiada). No hover/modal,
// entra junto com os campos principais no mesmo gráfico/lista de variação (ver
// `secondaryField` nos consumidores de `seriesOf`).
const SECONDARY_FIELDS = new Set(['remanescente_obra']);

export function secondaryField(ind) {
  if (!ind) return null;
  return valorFields(ind).find(f => SECONDARY_FIELDS.has(f)) || null;
}

// Flag "foi_apurado*" que a API manda pra cada campo de valor: `apurado` e
// `saldo_final` (campo principal) → `foi_apurado`; `apurado_X` → `foi_apurado_X`
// (ex.: apurado_sem_aporte → foi_apurado_sem_aporte). Demais campos (saldo_inicial,
// remanescente_obra) não têm flag.
export function foiApuradoKey(field) {
  if (field === 'apurado' || field === 'saldo_final') return 'foi_apurado';
  if (field.startsWith('apurado_')) return 'foi_' + field;
  return null;
}

export function fieldNaoApurado(ind, field) {
  const k = foiApuradoKey(field);
  return !!k && !!ind && ind[k] === false;
}

// nomes dos campos numéricos (não reservados) presentes num indicador. Campos não
// apurados (flag foi_apurado* = false) entram mesmo com valor null — e, se o campo
// principal nem veio no payload (ex.: Fundo Despesa sem saldo_final, LTV sem
// apurado), ele é incluído assim mesmo (saldo_final pros fundos em R$, apurado no resto), pra célula mostrar o status "Não apurado"
// em vez de cair em "Sem referência"/no saldo_inicial.
export function valorFields(ind) {
  if (!ind) return [];
  const fields = Object.keys(ind).filter(k => !RESERVED_FIELDS.has(k) &&
    (typeof ind[k] === 'number' || (ind[k] == null && fieldNaoApurado(ind, k))));
  if (ind.foi_apurado === false && !fields.includes('apurado') && !fields.includes('saldo_final'))
    fields.push(('saldo_inicial' in ind || ind.tipo_valor === 'reais') ? 'saldo_final' : 'apurado');
  return fields;
}

// Dentre os campos numéricos, escolhe os relevantes pra enquadramento/exibição:
// campos "apurado*" (pode haver mais de um, ex.: apurado_sem_aporte) são sempre o
// que importa; senão, fundos com saldo_inicial/saldo_final só mostram o saldo_final
// (o inicial é só contexto contábil do mês, não entra na regra de enquadramento).
function pickDisplayFields(fields) {
  const apurados = fields.filter(f => f.startsWith('apurado'));
  if (apurados.length) return apurados;
  if (fields.includes('saldo_final')) return ['saldo_final'];
  return fields;
}

export function displayFields(ind) {
  return pickDisplayFields(valorFields(ind));
}

// "valores em R$" / "valores em %" — mesma ideia de `unit` no dateline da
// Carteira (datelineTexto, CarteiraValue.jsx), adaptada pro `tipo_valor` de um
// indicador de Enquadramento (só existem esses dois tipos hoje).
export function unidadeTexto(tipoValor) { return tipoValor === 'reais' ? 'valores em R$' : 'valores em %'; }

// valor null = não apurado no mês (vale também pros gráficos de série histórica,
// que só têm o valor, não a flag foi_apurado*)
export function statusForValue(value, ref, tipoRef) {
  if (value == null) return 'nao_apurado';
  if (ref == null || tipoRef == null) return 'sem_ref';
  if (tipoRef === 'min') return value >= ref ? 'enquadrado' : 'desenquadrado';
  return value <= ref ? 'enquadrado' : 'desenquadrado';
}

export function statusForField(ind, field) {
  if (fieldNaoApurado(ind, field)) return 'nao_apurado';
  return statusForValue(ind[field], ind.ref, ind.tipo_ref);
}

// Pior status de uma lista (um por campo do indicador): desenquadrado > enquadrado
// > não apurado > sem referência. Um campo secundário não apurado (ex.: sem aporte)
// não "apaga" o status do campo principal já apurado.
export function worstStatus(statuses) {
  if (statuses.includes('desenquadrado')) return 'desenquadrado';
  if (statuses.includes('enquadrado')) return 'enquadrado';
  if (statuses.includes('nao_apurado')) return 'nao_apurado';
  return 'sem_ref';
}

// Cor de barra por status (verde=enquadrado, vermelho=desenquadrado, roxo=sem
// referência, cinza=não apurado) — quando o indicador tem mais de um apurado, cada campo usa um tom
// diferente da mesma cor, pra dar pra distinguir os campos sem trocar de família de cor.
const STATUS_SHADES = {
  enquadrado:    ['#8db632', '#5f7d1f', '#3f5614'],
  desenquadrado: ['#ff4848', '#d63333', '#a31f1f'],
  sem_ref:       ['#9e36ef', '#7d23c4', '#5c1a90'],
  nao_apurado:   ['#adb0b2', '#8d979e', '#717e86'],
};
export function statusBarColor(status, fieldIndex = 0) {
  const shades = STATUS_SHADES[status] || STATUS_SHADES.sem_ref;
  return shades[Math.min(fieldIndex, shades.length - 1)];
}

// Versão gradiente de statusBarColor — pras barras dos gráficos de série
// histórica dos indicadores (hover/card, IndicatorValue.jsx/MiniSpark em
// Painel2.jsx), mesmo espírito do gradiente da barra de Integralização de
// Cotas na Carteira. São `<rect>` de SVG, não `<div>` CSS — `fill` de SVG não
// entende `linear-gradient(...)` direto, precisa de um `<linearGradient>` em
// `<defs>` referenciado por `url(#id)`; por isso aqui, diferente de
// `statusBarGradient` (components/UI/index.jsx, usada em `<div>` CSS), a API
// é em duas partes: `statusBarGradientId` pro `fill`/`id`, e
// `STATUS_BAR_GRADIENT_COMBOS` (+ stops) pra desenhar os `<linearGradient>`
// uma vez por SVG, em `<defs>`. Escuro (tom seguinte, mais escuro) na base,
// vivo (tom do próprio fieldIndex) no topo — igual à barra vertical da
// Carteira/Enquadramento (ver statusBarGradient(status, 0)).
export function statusBarGradientId(status, fieldIndex = 0) {
  const shades = STATUS_SHADES[status] || STATUS_SHADES.sem_ref;
  return `bar-grad-${status}-${Math.min(fieldIndex, shades.length - 1)}`;
}
function statusBarGradientStops(status, fieldIndex) {
  const shades = STATUS_SHADES[status] || STATUS_SHADES.sem_ref;
  const i = Math.min(fieldIndex, shades.length - 1);
  return { dark: shades[Math.min(i + 1, shades.length - 1)], vivid: shades[i] };
}
// Todas as combinações status×fieldIndex existentes em STATUS_SHADES — usado
// pra desenhar todo `<linearGradient>` de uma vez em `<defs>` (poucos, baratos
// de desenhar sempre, mesmo os que a instância atual não usa).
export const STATUS_BAR_GRADIENT_COMBOS = Object.keys(STATUS_SHADES).flatMap(status =>
  STATUS_SHADES[status].map((_, fieldIndex) => ({ status, fieldIndex, ...statusBarGradientStops(status, fieldIndex) }))
);

// pior status entre os campos relevantes pra enquadramento (ver displayFields)
export function statusForIndicator(ind) {
  const fields = displayFields(ind);
  if (!fields.length) return 'sem_ref';
  return worstStatus(fields.map(f => statusForField(ind, f)));
}

export function indicatorHasBreach(ind) {
  return statusForIndicator(ind) === 'desenquadrado';
}

// achata fundos+indices de um split num único array [{key,grupo,...indicador}]
export function flattenSplitIndicators(split) {
  const out = [];
  ['fundos', 'indices'].forEach(grupo => {
    const dict = (split && split[grupo]) || {};
    Object.keys(dict).forEach(key => out.push({ key, grupo, ...dict[key] }));
  });
  return out;
}

export function findPeriodo(payload, periodo) {
  if (!payload) return null;
  return (payload.periodos || []).find(p => String(p.periodo) === String(periodo)) || null;
}

export function lastPeriodo(payload) {
  if (!payload || !payload.periodos || !payload.periodos.length) return null;
  return payload.periodos[payload.periodos.length - 1];
}

export function splitsAt(payload, periodo) {
  const p = findPeriodo(payload, periodo) || lastPeriodo(payload);
  return p ? (p.splits || []).map(s => ({ split_id: s.split_id, split_nome: s.split_nome, split_tipo: s.split_tipo })) : [];
}

export function splitAt(payload, periodo, splitId) {
  const p = findPeriodo(payload, periodo) || lastPeriodo(payload);
  return p ? (p.splits || []).find(s => s.split_id === splitId) || null : null;
}

export function indicatorsOfSplitAt(payload, periodo, splitId) {
  const sp = splitAt(payload, periodo, splitId);
  return sp ? flattenSplitIndicators(sp) : [];
}

export function indicatorAt(payload, periodo, splitId, grupo, key) {
  const sp = splitAt(payload, periodo, splitId);
  const ind = sp ? (sp[grupo] || {})[key] : null;
  return ind ? { ...ind, grupo, key } : null;
}

export function splitHasBreach(payload, periodo, splitId) {
  return indicatorsOfSplitAt(payload, periodo, splitId).some(indicatorHasBreach);
}

export function emissaoHasBreach(payload, periodo) {
  return splitsAt(payload, periodo).some(sp => splitHasBreach(payload, periodo, sp.split_id));
}

// catálogo de indicadores (colunas), únicos por grupo+key. `flattenSplitIndicators`
// já entrega fundos antes de índices por split, mas splits diferentes podem ser o
// "primeiro a aparecer" em períodos diferentes — por isso reordena no fim (sort
// estável) para garantir fundos sempre antes de razões/índices, independente de
// qual split apareceu primeiro.
export function catalogo(payload) {
  const seen = {}, list = [];
  (payload?.periodos || []).forEach(p => (p.splits || []).forEach(sp => flattenSplitIndicators(sp).forEach(ind => {
    const id = ind.grupo + ':' + ind.key;
    if (!seen[id]) { seen[id] = true; list.push({ key: ind.key, grupo: ind.grupo, nome: ind.nome, tipo_valor: ind.tipo_valor }); }
  })));
  return list.sort((a, b) => (a.grupo === 'fundos' ? 0 : 1) - (b.grupo === 'fundos' ? 0 : 1));
}

// série histórica de um indicador (split, grupo, key) ao longo de todos os períodos do payload.
// porCampo[field] é um array alinhado a `periodos`, com null nos meses sem aquele campo.
export function seriesOf(payload, splitId, grupo, key) {
  const periodosArr = payload?.periodos || [];
  const periodos = periodosArr.map(p => p.periodo);
  const indsByIdx = periodosArr.map(p => {
    const sp = (p.splits || []).find(s => s.split_id === splitId);
    return sp ? (sp[grupo] || {})[key] || null : null;
  });
  const allFieldsRaw = new Set();
  indsByIdx.forEach(ind => { if (ind) valorFields(ind).forEach(f => allFieldsRaw.add(f)); });
  const fields = pickDisplayFields([...allFieldsRaw]);
  const secField = [...allFieldsRaw].find(f => SECONDARY_FIELDS.has(f)) || null;
  const porCampo = {};
  fields.forEach(f => { porCampo[f] = indsByIdx.map(ind => (ind && ind[f] != null) ? ind[f] : null); });
  if (secField) porCampo[secField] = indsByIdx.map(ind => (ind && ind[secField] != null) ? ind[secField] : null);
  const refSeries = indsByIdx.map(ind => (ind && ind.ref != null) ? ind.ref : null);
  let meta = null;
  for (let i = indsByIdx.length - 1; i >= 0; i--) { if (indsByIdx[i]) { meta = indsByIdx[i]; break; } }
  return {
    periodos, porCampo, refSeries, fields, secField,
    nome: meta?.nome, tipo_valor: meta?.tipo_valor, ref: meta?.ref, tipo_ref: meta?.tipo_ref,
  };
}
